import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../auth/useSession";
import { useCreator } from "../creator/CreatorContext";
import { Button, Display, Panel, Sprite } from "../design/primitives";
import { art } from "../design/sprites";
import css from "./landing.module.css";

/**
 * §22 / FR: hero, scanline overlay on the hero **only**, "behind the door"
 * strip. The tab count is computed; the cadence phrase comes from the creator
 * record (D-9) - neither is a literal.
 */
export default function Landing() {
  const { creator } = useCreator();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Where the visitor was actually headed, put here by `RequireSession` when it
   * turned them away. Not validated at this point: `signIn` refuses anything
   * that is not a same-origin path, and doing it in one place means there is
   * one rule rather than one per call site (NFR-3).
   */
  const requestedPath = (location.state as { from?: string } | null)?.from;
  // From the public payload. It used to call listAssets(), which needs a token
  // - and this is the one screen whose whole audience has none.
  const count = creator.assetCount ?? null;

  return (
    <div className={css.page}>
      <section className={css.hero}>
        <div className={css.scanlines} aria-hidden />

        <div className={css.heroCopy}>
          <p className={css.cadence}>
            {creator.landingCopy.cadence.toUpperCase()}
            {count !== null && ` · ${count} ARRANGEMENTS`}
          </p>
          <Display size={62} lineHeight={1.05} as="h1">
            {creator.landingCopy.headline}
          </Display>
          <p className={css.lede}>
            Tabs and arrangements for the {creator.displayName} channel, in PDF
            and Guitar Pro. Sign in with Patreon and the ones your tier unlocks
            are already here.
          </p>
          {/* Signing in round-trips through our own /auth/start to Patreon and
              back — no identity provider in between since task 9. */}
          {session.isAuthenticated ? (
            <Button variant="gold" size="landing" onClick={() => navigate("/tabs")}>
              ENTER THE LIBRARY
            </Button>
          ) : (
            <Button
              variant="gold"
              size="landing"
              disabled={session.isLoading}
              /*
                The path a signed-out visitor asked for, if they were sent here
                from one. `RequireSession` puts it in navigation state; before
                this, nothing read it and the request was lost at the door
                (FR-2).
              */
              onClick={() => session.signIn(requestedPath)}
            >
              SIGN IN WITH PATREON
            </Button>
          )}
        </div>

        <div className={css.heroArt}>
          <Sprite src={art.qblock} size={220} />
        </div>
      </section>

      <section className={css.strip}>
        <div className={css.stripInner}>
          <Display size={38} as="h2">
            BEHIND THE DOOR
          </Display>
          <div className={css.perks}>
            <Panel className={css.perk}>
              <Sprite src={art.pdf} size={48} label="PDF" />
              <h3 className={css.perkTitle}>Every arrangement as a PDF</h3>
              <p className={css.perkBody}>
                Notation and tab on the same page, laid out to be read from a
                stand rather than scrolled on a phone.
              </p>
            </Panel>
            <Panel className={css.perk}>
              <Sprite src={art.gp} size={48} label="Guitar Pro" />
              <h3 className={css.perkTitle}>Guitar Pro files</h3>
              <p className={css.perkBody}>
                Slow it down, loop the bar that is fighting you, mute a voice
                and play the other one.
              </p>
            </Panel>
            <Panel className={css.perk}>
              <Sprite src={art.cassette} size={48} />
              <h3 className={css.perkTitle}>Honest difficulty</h3>
              <p className={css.perkBody}>
                Five pips, absolute. One is an afternoon. Five took me years and
                the tab says so.
              </p>
            </Panel>
            <Panel className={css.perk}>
              <Sprite src={art.crt} size={48} />
              <h3 className={css.perkTitle}>The performance</h3>
              <p className={css.perkBody}>
                Where a recording exists, the tab links straight to it, so you
                can hear what it is meant to sound like.
              </p>
            </Panel>
          </div>
        </div>
      </section>

      <footer className={css.footerLinks}>
        <Link to="/privacy-policy" className={css.footerLink}>
          PRIVACY POLICY
        </Link>
        <Link to="/terms-of-service" className={css.footerLink}>
          TERMS OF SERVICE
        </Link>
      </footer>
    </div>
  );
}
